import { get } from 'lodash';
import { AppRecordValidateUpdate } from 'routes';
import recordNamePathsWithRequiredPointOfContacts from 'libs/recordNamePathsWithRequiredPointOfContacts';
import { sharedValidateUsEntrySummary } from '../sharedValidateUsEntrySummary';
import { PreferredDutyCollectPartyType } from '@xbcb/finance-types';
import { RecordType } from '@xbcb/shared-types';
import { formatRecordName } from '@xbcb/js-utils';
import { NRBS_RECORD_NUMBER_LEN } from 'libs/constant';
import { isSection232AluminumSmeltAndCastApplicableForAnyTariff } from '../htsHelpers/isSection232AluminiumSmeltAndCastApplicable';
import { formatHTS } from 'libs/htsHelpers';
import { section232AluminumRequirementsFormFieldsNamePaths } from '../../components/AluminumSmeltAndCastCountryDetail/component';
import {
  formatMomentAsEffectiveDateString,
  DEFAULT_EFFECTIVE_DATE,
} from '../dateUtils';
import { validateTarrifPga } from 'libs/validateTariffPga';

// Edit this if any of the pga forms are changed/added
// TODO There is a lot of duplication of logic here and individual forms. Try to abstract the logic for required fields
const validateUsConsumptionEntry: AppRecordValidateUpdate = ({
  input,
  existingRecord,
  currentUser,
  location,
  store,
}) => {
  const { invoices, buyer, ior } = input;
  const { accountType } = currentUser;

  const {
    additionalErrors,
    validateFields,
    validatePartySnapshot,
    validateTabs,
  } = sharedValidateUsEntrySummary({
    input,
    currentUser,
  });
  const iorName = formatRecordName({
    recordType: RecordType.US_IOR,
    accountType,
  });
  if (validateTabs.commercialInvoices) {
    validatePartySnapshot(['ior', 'usIor', 'id'], iorName, [
      ['preferredPaymentMethod'],
    ]);
    if (!buyer?.sameAsIor) {
      validatePartySnapshot(['buyer', 'buyer', 'id'], 'Buyer');
      const validDunsLengths = [9, 13];
      if (!validDunsLengths.includes(buyer?.duns?.length)) {
        additionalErrors.push({
          title: 'Buyer DUNS Number',
          messages: [`Invalid DUNS number "${buyer?.duns}"`],
        });
      }
    }

    const preferredDutyCollectPartyType =
      input?.group?.shipment?.preferredDutyCollectPartyType;

    invoices?.forEach((invoice: any, invoiceIndex: number) => {
      invoice.products?.forEach((product: any, productIndex: number) => {
        product?.lines?.forEach((line: any, lineIndex: number) => {
          const storeState = store?.getState();
          const estimatedImportDateString = input.arrival.importDate
            ? formatMomentAsEffectiveDateString(input.arrival.importDate)
            : DEFAULT_EFFECTIVE_DATE;
          const effectiveDateHtsHierarchy =
            storeState?.hts?.usHtsData?.[estimatedImportDateString];

          const htsObjects =
            line.tariffs?.map(
              (tariff: any) =>
                effectiveDateHtsHierarchy?.[formatHTS(tariff.htsNumber)] ?? {},
            ) || [];

          if (
            isSection232AluminumSmeltAndCastApplicableForAnyTariff(
              htsObjects,
              input.arrival.importDate?.toDate(),
            )
          ) {
            const linePath = [
              'invoices',
              invoiceIndex,
              'products',
              productIndex,
              'lines',
              lineIndex,
            ];
            section232AluminumRequirementsFormFieldsNamePaths.forEach(
              (fieldNamePath) => {
                validateFields.push([...linePath, ...fieldNamePath]);
              },
            );
          }

          if (
            line?.nrbsRecordNumber &&
            line.nrbsRecordNumber.length !== NRBS_RECORD_NUMBER_LEN
          ) {
            additionalErrors.push({
              title: 'Nrbs Recording Number',
              messages: [
                `Nrbs Record Number must be 10 alphanumeric characters in line ${
                  lineIndex + 1
                }`,
              ],
            });
          }

          line.tariffs?.forEach((tariff: any, tariffIndex: number) => {
            const tariffNamePath = [
              'invoices',
              invoiceIndex,
              'products',
              productIndex,
              'lines',
              lineIndex,
              'tariffs',
              tariffIndex,
            ];
            validateFields.push([...tariffNamePath, 'pgaDescription', 'text']);
            const tariffErrorString = `Invoice ${invoiceIndex + 1}, product ${
              productIndex + 1
            }, line ${lineIndex + 1}, tariff ${tariffIndex + 1}`;
            if (tariff.pga) {
              const {
                validateFields: validateFieldsToAppend,
                additionalErrors: additionalErrorsToAppend,
              } = validateTarrifPga({
                tariff,
                iorName,
                validatePartySnapshot,
                ior,
                tariffNamePath,
                tariffErrorString,
                line,
              });
              validateFields.push(...validateFieldsToAppend);
              additionalErrors.push(...additionalErrorsToAppend);
            }
          });
        });
      });
    });

    //  if preferred duty collection type is CUSTOMS then IOR needs to have a PUN
    if (
      preferredDutyCollectPartyType === PreferredDutyCollectPartyType.CUSTOMS &&
      ior &&
      !ior.payerUnitNumber
    ) {
      additionalErrors.push({
        title: 'Payer Unit Number Missing',
        messages: [
          `Duty Collect Party is set to Customs, but the ${iorName}'s profile does not have a Payer Unit Number. Please enter a valid Payer Unit Number into the ${iorName}'s profile, or change the Duty Collect Party.`,
        ],
      });
    }
    // recordNamePathsWithRequiredPointOfContacts returns NamePath[] where each NamePath
    // is the path to the record (for example `['ior']`) which allows
    // easy access to the record info (ior) as well as the `pointOfContact`
    const recordNamePaths =
      recordNamePathsWithRequiredPointOfContacts(input) || [];
    for (const recordNamePath of recordNamePaths) {
      const recordInfo = get(input, recordNamePath, {});
      const { name, email, phone } = recordInfo?.pointOfContact || {};
      if (!name || !email || !phone?.number) {
        additionalErrors.push({
          title: 'Point of Contact',
          messages: [
            `Please fill out "point of contact" info for ${recordInfo.name} on their page, save, then refresh their dropdown on this page.`,
          ],
        });
      }
    }
  }

  return {
    additionalErrors,
    validateFields,
  };
};

export default validateUsConsumptionEntry;
